import { forwardRef, useEffect, useRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  indeterminate?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, indeterminate = false, id, ...props }, forwardedRef) => {
    const inputId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const internalRef = useRef<HTMLInputElement>(null);

    // Set indeterminate state
    useEffect(() => {
      const node = internalRef.current;
      if (node) {
        node.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <div className="flex items-center">
        <input
          ref={(node) => {
            internalRef.current = node;
            if (typeof forwardedRef === 'function') {
              forwardedRef(node);
            } else if (forwardedRef) {
              forwardedRef.current = node;
            }
          }}
          type="checkbox"
          id={inputId}
          className={cn(
            'h-4 w-4 rounded border-border-subtle bg-dark-elevated-2 text-accent',
            'focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-dark-base',
            'checked:bg-accent checked:border-accent',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          {...props}
        />
        {label && (
          <label
            htmlFor={inputId}
            className="ml-2 text-sm text-text-secondary cursor-pointer select-none"
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
